// Minnesotaänger Mini App — message API HTTP client.
// Contract source of truth: minnesanger-miniapp-v1/03-api-draft.md (shared contract).
// New-in-miniapp: written for this project, not from telegram-tt.
//
// Auth: Telegram Mini App init data (validated server-side by miniapp_auth.py).
// Outside the Telegram client (local dev / tests) the init data may be provided
// explicitly for the dev server; see src/api/miniapp/README notes in index.tsx.

import {
  ApiMiniAppEditRequest,
  ApiMiniAppError,
  ApiMiniAppErrorBody,
  ApiMiniAppHistoryResponse,
  ApiMiniAppConversationsResponse,
  ApiMiniAppSendRequest,
  ApiMiniAppTargetRequest,
  ApiMiniAppAcceptedResponse,
} from './types';

export { ApiMiniAppError } from './types';

// Production deploys behind a path prefix (e.g. https://vocab.rincloud.xyz/chat/),
// where the API lives at <base>api/v1. The fallback is derived from Vite's own
// BASE_URL so no build step has to hand-remember it: base '/chat/' → '/chat/api/v1',
// dev '/' → '/api/v1' (vite proxies /api/v1 to the local API server).
// An explicit VITE_API_BASE still wins (highest priority).
export const MINIAPP_API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || `${import.meta.env.BASE_URL}api/v1`;

export function getMiniAppInitData(): string {
  // Inside Telegram: WebApp.initData is injected by the client.
  // Dev fallback (local preview without Telegram): look for a script-injected
  // global or a dev cookie; absent → empty string, server decides (403 for
  // real auth, but the dev-mode API backend accepts a test init data).
  const w = window as typeof window & {
    Telegram?: { WebApp?: { initData?: string } };
    MINIAPP_INIT_DATA?: string;
  };
  const fromTelegram = w.Telegram?.WebApp?.initData;
  if (fromTelegram) return fromTelegram;
  if (w.MINIAPP_INIT_DATA) return w.MINIAPP_INIT_DATA;
  return '';
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const initData = getMiniAppInitData();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  };
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  }

  let response: Response;
  try {
    response = await fetch(`${MINIAPP_API_BASE}${path}`, {
      ...options,
      headers,
    });
  } catch (err) {
    throw new ApiMiniAppError('INTERNAL', `Network error: ${(err as Error).message}`);
  }

  if (!response.ok) {
    let body: ApiMiniAppErrorBody | undefined;
    try {
      body = await response.json() as ApiMiniAppErrorBody;
    } catch {
      // Non-JSON error body
    }
    if (body?.error?.code) {
      throw new ApiMiniAppError(body.error.code, body.error.message, body.error.detail);
    }
    throw new ApiMiniAppError('INTERNAL', `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const miniappApi = {
  // GET /api/v1/conversations
  getConversations(): Promise<ApiMiniAppConversationsResponse> {
    return request<ApiMiniAppConversationsResponse>('/conversations');
  },

  // GET /api/v1/conversations/{chat_id}/messages?before_event_id=&limit=
  getMessages(
    chatId: string,
    beforeEventId?: number,
    limit = 50,
  ): Promise<ApiMiniAppHistoryResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (beforeEventId !== undefined && beforeEventId !== null) {
      params.set('before_event_id', String(beforeEventId));
    }
    const encodedChatId = encodeURIComponent(chatId);
    return request<ApiMiniAppHistoryResponse>(
      `/conversations/${encodedChatId}/messages?${params.toString()}`,
    );
  },

  // POST /api/v1/send — 202 Accepted, async: refresh history to observe result
  send(payload: ApiMiniAppSendRequest): Promise<ApiMiniAppAcceptedResponse> {
    return request<ApiMiniAppAcceptedResponse>('/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  // POST /api/v1/reroll
  reroll(payload: ApiMiniAppTargetRequest): Promise<ApiMiniAppAcceptedResponse> {
    return request<ApiMiniAppAcceptedResponse>('/reroll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  // POST /api/v1/edit
  edit(payload: ApiMiniAppEditRequest): Promise<ApiMiniAppAcceptedResponse> {
    return request<ApiMiniAppAcceptedResponse>('/edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  // POST /api/v1/delete
  delete(payload: ApiMiniAppTargetRequest): Promise<ApiMiniAppAcceptedResponse> {
    return request<ApiMiniAppAcceptedResponse>('/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },
};
